import type { GetRequestsListResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import apiClient from "@/lib/api/client";

import type { TeamLeadFilterType } from "../../components/TeamLeadDropdownFilter";

type TeamLeadSectionType =
  | "awaiting-reply"
  | "in-queue"
  | "others"
  | "processing";
type TeamLeadTabType =
  | "all-requests"
  | "assigned-to-me"
  | "unassigned-requests";
type ViewModeType = "section" | "single-table";

interface UseTeamLeadRequestDataParams {
  /**
   * Pagination and sorting options
   */
  direction?: "asc" | "desc";
  enabled?: boolean;
  /**
   * The filter applied from the dropdown (all, completed, open, overdue, due-today)
   */
  filter: TeamLeadFilterType;
  limit?: number;
  page?: number;
  search?: string;
  /**
   * The section to display (only used in "section" viewMode)
   */
  section?: TeamLeadSectionType;
  skipCount?: boolean;
  sortBy?: string;
  /**
   * The Team Lead tab being displayed
   * - "assigned-to-me": Requests assigned to the current team lead
   * - "all-requests": All team requests (assigned + unassigned)
   * - "unassigned-requests": Only unassigned requests
   */
  tab: TeamLeadTabType;
  /**
   * Optional user ID override. When provided, fetches requests for this user
   * instead of the logged-in user. Used when viewing another user's profile.
   */
  userId?: string;
  /**
   * The view mode determines whether to use sections or single-table
   * - "section": Multi-section mode (Processing, Awaiting Reply, In Queue, Others)
   * - "single-table": Single-table mode (completed, due-today, overdue)
   */
  viewMode: ViewModeType;
}

/**
 * Unified hook for fetching Team Lead request data
 * Consolidates all Team Lead data fetching into a single configurable hook
 *
 * Replaces:
 * - useTeamLeadSectionData
 * - useTeamLeadSingleTableData
 * - useTeamLeadAllRequestsSectionData
 * - useTeamLeadAllRequestsSingleTableData
 * - useTeamLeadUnassignedRequestsSingleTableData
 *
 * @example
 * // Assigned to Me - Section Mode
 * useTeamLeadRequestData({
 *   tab: "assigned-to-me",
 *   viewMode: "section",
 *   section: "processing",
 *   filter: "all"
 * })
 *
 * @example
 * // All Requests - Single Table Mode
 * useTeamLeadRequestData({
 *   tab: "all-requests",
 *   viewMode: "single-table",
 *   filter: "completed"
 * })
 */
export function useTeamLeadRequestData({
  direction = "desc",
  enabled = true,
  filter,
  limit = 10,
  page = 1,
  search = "",
  section,
  skipCount,
  sortBy = "createdAt",
  tab,
  userId,
  viewMode,
}: UseTeamLeadRequestDataParams) {
  return useQuery({
    enabled,
    queryFn: async () => {
      const params: Record<string, string> = {
        direction,
        limit: limit.toString(),
        page: page.toString(),
        sortBy,
        tab,
        teamLeadFilter: filter,
      };

      // When viewing another user's requests, pass forUserId to override context
      if (userId) {
        params.forUserId = userId;
      }

      // Only include teamLeadSection for section-based views
      if (viewMode === "section" && section) {
        params.teamLeadSection = section;
      }

      // Include search if provided
      if (search) {
        params.search = search;
      }

      const shouldSkipCount = skipCount ?? page > 1;

      params.skipCount = shouldSkipCount.toString();

      const queryString = new URLSearchParams(params).toString();

      return await apiClient
        .url(`/requests?${queryString}`)
        .get()
        .json<GetRequestsListResponse>();
    },
    queryKey: [
      "teamLeadRequests",
      tab,
      viewMode,
      ...(viewMode === "section" && section ? [section] : ["single-table"]),
      filter,
      page,
      limit,
      sortBy,
      direction,
      search,
      skipCount ?? page > 1,
      ...(userId ? [userId] : []),
    ],
    staleTime: 30_000, // 30 seconds
  });
}
